/* Noah.Q v341 — instant category-to-category navigation.
 * Keeps the current page visible while the next collection is fetched,
 * then swaps MainContent without a full white document reload.
 * Scope is deliberately limited to /collections/* -> /collections/*.
 */
(() => {
  'use strict';

  const isCollectionPath = (pathname) => /^\/collections(?:\/|$)/.test(pathname);
  const sameOrigin = (url) => url.origin === window.location.origin;
  let controller = null;
  let navigating = false;

  const updateHead = (doc) => {
    if (doc.title) document.title = doc.title;
    const nextCanonical = doc.querySelector('link[rel="canonical"]');
    const currentCanonical = document.querySelector('link[rel="canonical"]');
    if (nextCanonical && currentCanonical) currentCanonical.href = nextCanonical.href;
  };

  const updateHeaderNav = (doc) => {
    const nextDesktop = doc.querySelector('.nqs-header-nav');
    const currentDesktop = document.querySelector('.nqs-header-nav');
    if (nextDesktop && currentDesktop) currentDesktop.innerHTML = nextDesktop.innerHTML;

    const nextMobile = doc.querySelector('.menu-drawer__menu');
    const currentMobile = document.querySelector('.menu-drawer__menu');
    if (nextMobile && currentMobile) currentMobile.innerHTML = nextMobile.innerHTML;
  };

  const closeDrawer = () => {
    const details = document.querySelector('header-drawer details[open]');
    if (!details) return;
    const summary = details.querySelector('summary');
    const drawer = details.closest('header-drawer');
    if (drawer && typeof drawer.closeMenuDrawer === 'function' && summary) {
      drawer.closeMenuDrawer(new Event('click'), summary);
    } else {
      details.removeAttribute('open');
    }
  };

  const swapCollection = async (url, { push = true } = {}) => {
    if (navigating) controller?.abort();
    controller = new AbortController();
    navigating = true;

    const main = document.getElementById('MainContent');
    if (!main) {
      window.location.assign(url.href);
      return;
    }

    main.setAttribute('aria-busy', 'true');
    document.documentElement.dataset.nqCategoryLoading = 'true';

    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'NoahQ-Category-Navigation' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextMain = doc.getElementById('MainContent');
      if (!nextMain) throw new Error('MainContent missing');

      updateHead(doc);
      updateHeaderNav(doc);
      closeDrawer();

      main.replaceChildren(...Array.from(nextMain.childNodes).map(node => node.cloneNode(true)));
      main.removeAttribute('aria-busy');

      if (push) history.pushState({ nqCategory: true }, '', url.href);

      // Category pages share the same loaded collection scripts; new custom elements
      // connect automatically when inserted. Re-announce the updated main region.
      main.dispatchEvent(new CustomEvent('noahq:category:navigated', {
        bubbles: true,
        detail: { url: url.href }
      }));

      const header = document.querySelector('.section-header, sticky-header, header.nqs-header');
      const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      const y = Math.max(0, main.getBoundingClientRect().top + window.scrollY - headerHeight);
      window.scrollTo({ top: y, behavior: 'auto' });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      window.location.assign(url.href);
    } finally {
      navigating = false;
      document.documentElement.removeAttribute('data-nq-category-loading');
      main.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a.nq-category-link[href]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    if (!sameOrigin(url)) return;
    if (!isCollectionPath(window.location.pathname) || !isCollectionPath(url.pathname)) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;

    event.preventDefault();
    swapCollection(url);
  });

  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);
    if (!isCollectionPath(url.pathname)) return;
    swapCollection(url, { push: false });
  });
})();
